import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormFieldCategory } from 'src/app/enum/form-field-category';
import { CheckListField } from 'src/app/model/checklist-field';
import { Event } from 'src/app/model/event';
import { Submission } from 'src/app/model/paper';
import { AuthService } from 'src/app/service/auth.service';
import { PublicationService, SubmissionCoverage } from 'src/app/service/publication.service';

@Component({
  selector: 'app-paper-publication-info',
  templateUrl: './paper-publication-info.component.html',
  styleUrls: ['./paper-publication-info.component.scss']
})
export class PaperPublicationInfoComponent implements OnInit, OnChanges {

  @Output() IEEECopyright = new EventEmitter();

  /** Marcação alterada em modo preview — o consumidor sincroniza as outras visões. */
  @Output() previewChange = new EventEmitter<void>();

  @Input() covers: SubmissionCoverage[];
  @Input() submission: Submission;
  @Input() showIeeeCopyright = false;
  @Input() isAdminOrChair = false;
  @Input() isSessionChair = false;
  @Input() isAuthor = false;
  // Preview somente-leitura (chair pré-visualizando outro perfil): nenhum item é editável.
  @Input() readOnly = false;

  /**
   * Pré-visualização SEM submissão real (Controles de publicação): os itens
   * renderizam com as regras verdadeiras de `canEdit` — é justamente isso que se
   * quer mostrar —, mas marcar um deles não grava nada. Difere de `readOnly`,
   * que desabilita tudo e por isso esconderia a diferença entre "vê" e "edita".
   */
  @Input() previewOnly = false;

  event: Event;

  form: FormGroup;

  FormFieldCategory = FormFieldCategory;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private publicationService: PublicationService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.event = this.submission?.event;
    if (this.isAdminOrChair == undefined) {
      this.authService.userIsAdminOrChair(this.event.id).subscribe(isAdminOrChair => {
        this.isAdminOrChair = isAdminOrChair;
        this.initForm();  
      });
    } else {
      this.initForm();  
    }
  }

  initForm() {
    this.form = this.fb.group({});
    this.covers.forEach(cover => {
      this.form.addControl(cover.field.id.toString(), new FormControl({value: cover.answer, disabled: !this.canEdit(cover.field)}));
    });

    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.controls[controlName];
      const coverage = this.covers.filter(c => c.field.id == parseInt(controlName))[0];

      control.valueChanges.subscribe(() => {
        this.toggleCover(coverage);
      });
    });
  }

  // Reaplica o estado disabled dos checkboxes quando `readOnly` muda (o chair
  // pode alternar o preview em runtime). `emitEvent:false` p/ não disparar o
  // toggleCover no valueChanges.
  ngOnChanges(changes: SimpleChanges): void {
    // Lista de itens trocada (o chair mexeu nos Controles de publicação e o
    // preview precisa acompanhar): remonta o form sobre os novos covers.
    if (changes.covers && !changes.covers.firstChange) {
      this.initForm();
      return;
    }

    if (changes.readOnly && this.form) {
      this.covers.forEach(cover => {
        const control = this.form.get(cover.field.id.toString());
        if (control) {
          this.canEdit(cover.field) ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
        }
      });
    }
  }

  canEdit(field: CheckListField) {
    if (this.readOnly) { return false; }   // preview do chair → só leitura
    const authorCanEdit = field.modifiableBy.author && this.isAuthor;
    const sessionChairCanEdit = field.modifiableBy.sessionChair && this.isSessionChair;

    return ((this.isAdminOrChair || sessionChairCanEdit) && !this.isAuthor) || authorCanEdit;
  }

  toggleCover(cover: SubmissionCoverage) {

    if (!this.canEdit(cover.field)) {
      return;
    }

    // Preview: reflete a marcação no MODELO, mas não persiste — não há submissão
    // por trás. Atualizar o modelo é o que faz o chip de prontidão da seção
    // reagir, então o chair vê "Pendente" virar "Pronta" ao experimentar.
    if (this.previewOnly) {
      cover.answer = this.form.get(cover.field.id.toString()).value;
      this.previewChange.emit();
      return;
    }

    const data = {
      'checklist': cover.field.id,
      'submission': this.submission.id
    };

    this.publicationService.toggleSubmissionAnswer(this.event.id, data).subscribe(newAnswer => {
      cover.answer = newAnswer;
    });

  }

  getCoverageTitle(coverage: SubmissionCoverage): string {
    switch(coverage.field.category) {
      case FormFieldCategory.AUTHOR:
        return this.translate.instant('admin.event.publication.controls.registration');
      case FormFieldCategory.COPYRIGHT:
        return this.translate.instant('admin.event.publication.controls.copyright');
      case FormFieldCategory.NO_SHOW:
        // Faltava: a categoria nasceu com a unificação dos Controles de
        // publicação e o switch não a conhecia — o item aparecia SEM rótulo,
        // aqui e na página da submissão.
        return this.translate.instant('admin.event.publication.controls.no-show');
      case FormFieldCategory.CUSTOM:
        return coverage.field.description;
    }
  }

  submitToIEEE() {
    this.IEEECopyright.emit();
  }

}
